import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, ArrowLeft, LogOut } from "lucide-react";
import { isAdminAuthed, clearAdminAuth } from "./AdminLandingPage";
import ClientAccessManager from "../components/ClientAccessManager";

export default function ClientAccessPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Client Access — Immense Estate Admin";
    if (!isAdminAuthed()) {
      navigate("/admin");
    }
  }, [navigate]);

  const logout = () => {
    clearAdminAuth();
    navigate("/admin");
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-navy-900 text-white">
      {/* Ambient glows */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-emerald-500/10 blur-[120px]" />
        <div className="absolute -right-56 top-24 h-[680px] w-[680px] rounded-full bg-sky-500/8 blur-[120px]" />
      </div>

      <header className="relative z-10 flex items-center justify-between px-6 py-5 md:px-10">
        <button
          onClick={() => navigate("/admin")}
          className="flex items-center gap-3 transition hover:opacity-80"
        >
          <div className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm">
            <Shield className="h-5 w-5 text-amber-400" />
          </div>
          <div className="leading-tight text-left">
            <div className="text-[10px] font-semibold tracking-[0.3em] text-zinc-500 uppercase">Immense Estate</div>
            <div className="text-sm font-semibold text-white">Administration</div>
          </div>
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-white/80 transition hover:bg-white/10"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
          <button
            onClick={logout}
            className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-white/80 transition hover:bg-white/10"
          >
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </header>

      <main className="relative z-10 mx-auto w-full max-w-4xl px-6 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="mb-2 text-[11px] font-bold tracking-[0.3em] text-emerald-400 uppercase">
            Access Control
          </div>
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Client Access</h1>
          <p className="mt-2 text-sm text-white/50">
            Create and manage client credentials. Copy them to send via email. Lock or remove access at any time.
          </p>

          <ClientAccessManager />
        </motion.div>
      </main>
    </div>
  );
}
